import { useEffect, useState, type FormEvent } from "react";
import { ApiError } from "@/lib/api";
import { Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { useCreateUser, useDepartments, useRoles, useUpdateUser } from "./hooks";
import { useSites } from "@/features/sites/hooks";
import { uploadUserPhoto, type ManagedUser } from "./api";
import { UserAvatar } from "./UserAvatar";

const MIN_PASSWORD = 12;

interface Props {
  open: boolean;
  onClose: () => void;
  editing: ManagedUser | null;
  onCreated: (temporaryPassword: string, email: string) => void;
}

export function UserFormDialog({ open, onClose, editing, onCreated }: Props) {
  const { data: roles } = useRoles();
  const { data: departments } = useDepartments();
  const { data: sites } = useSites({ enabled: open });
  const create = useCreateUser();
  const update = useUpdateUser();
  const [siteIds, setSiteIds] = useState<string[]>([]);

  const [form, setForm] = useState({
    email: "",
    password: "",
    full_name: "",
    role_code: "support_engineer",
    designation: "",
    phone: "",
    department_id: "",
    customer_id: "",
    employee_code: "",
    joining_date: "",
    date_of_birth: "",
    blood_group: "",
    address: "",
    emergency_contact: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoKey, setPhotoKey] = useState(0);

  async function handlePhoto(file: File) {
    if (!editing) return;
    setError(null);
    setPhotoBusy(true);
    try {
      await uploadUserPhoto(editing.id, file);
      // editing.has_photo may still be false in this render; bump the key and
      // set it locally so the avatar refetches the new image.
      editing.has_photo = true;
      setPhotoKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not upload the photo.");
    } finally {
      setPhotoBusy(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSiteIds(editing?.sites?.map((l) => l.id) ?? []);
    setForm({
      email: editing?.email ?? "",
      password: "",
      full_name: editing?.full_name ?? "",
      role_code: editing?.role_code ?? "support_engineer",
      designation: editing?.designation ?? "",
      phone: editing?.phone ?? "",
      department_id: editing?.department_id ?? "",
      customer_id: editing?.customer_id ?? "",
      employee_code: editing?.employee_code ?? "",
      joining_date: editing?.joining_date ?? "",
      date_of_birth: editing?.date_of_birth ?? "",
      blood_group: editing?.blood_group ?? "",
      address: editing?.address ?? "",
      emergency_contact: editing?.emergency_contact ?? "",
    });
  }, [open, editing]);

  const isCustomerRole = form.role_code === "customer";
  // Staff who get urgent-ticket WhatsApp alerts need a phone on file.
  const phoneForAlerts = ["manager", "support_engineer", "field_engineer"].includes(
    form.role_code,
  );
  const phoneMissing = phoneForAlerts && !form.phone.trim();
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Mirrors the server rule so the user gets feedback before a round trip.
    // The server remains the authority — this is convenience, not enforcement.
    if (isCustomerRole && !form.customer_id.trim()) {
      setError("A customer account must be linked to a customer.");
      return;
    }

    const employee = {
      employee_code: form.employee_code || null,
      joining_date: form.joining_date || null,
      date_of_birth: form.date_of_birth || null,
      blood_group: form.blood_group || null,
      address: form.address || null,
      emergency_contact: form.emergency_contact || null,
    };

    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          body: {
            full_name: form.full_name,
            phone: form.phone || null,
            designation: form.designation || null,
            department_id: form.department_id || null,
            role_code: form.role_code,
            ...employee,
            // Staff only; customers are never site-scoped.
            ...(isCustomerRole ? {} : { site_ids: siteIds }),
          },
        });
      } else {
        const res = await create.mutateAsync({
          email: form.email.trim(),
          password: form.password,
          full_name: form.full_name.trim(),
          role_code: form.role_code,
          phone: form.phone || null,
          designation: form.designation || null,
          department_id: form.department_id || null,
          customer_id: isCustomerRole ? form.customer_id.trim() : null,
          ...employee,
          ...(isCustomerRole ? {} : { site_ids: siteIds }),
        });
        onCreated(res.temporary_password, res.user.email);
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  const busy = create.isPending || update.isPending;
  const passwordTooShort = !editing && form.password.length > 0 && form.password.length < MIN_PASSWORD;

  return (
    <Dialog open={open} onClose={onClose} title={editing ? "Edit user" : "Add user"}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        <Field label="Full name" htmlFor="full_name">
          <Input
            id="full_name"
            required
            value={form.full_name}
            onChange={(e) => set("full_name")(e.target.value)}
          />
        </Field>

        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            required
            // Email is the login identity and the unique key; changing it is
            // an account-takeover vector, so it is create-only.
            disabled={editing !== null}
            value={form.email}
            onChange={(e) => set("email")(e.target.value)}
          />
        </Field>

        {!editing && (
          <Field
            label={`Initial password (min ${MIN_PASSWORD} characters)`}
            htmlFor="password"
            error={passwordTooShort ? `Must be at least ${MIN_PASSWORD} characters.` : undefined}
          >
            <Input
              id="password"
              type="text"
              required
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => set("password")(e.target.value)}
            />
          </Field>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Role" htmlFor="role_code">
            <Select
              id="role_code"
              value={form.role_code}
              onChange={(e) => set("role_code")(e.target.value)}
            >
              {(roles ?? []).map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Department" htmlFor="department_id">
            <Select
              id="department_id"
              value={form.department_id}
              onChange={(e) => set("department_id")(e.target.value)}
            >
              <option value="">— none —</option>
              {(departments ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {isCustomerRole && (
          <Field label="Customer ID" htmlFor="customer_id">
            <Input
              id="customer_id"
              placeholder="UUID of the customer organisation"
              disabled={editing !== null}
              value={form.customer_id}
              onChange={(e) => set("customer_id")(e.target.value)}
            />
          </Field>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Designation" htmlFor="designation">
            <Input
              id="designation"
              value={form.designation}
              onChange={(e) => set("designation")(e.target.value)}
            />
          </Field>
          <Field
            label={phoneForAlerts ? "Phone (for WhatsApp alerts)" : "Phone"}
            htmlFor="phone"
            error={phoneMissing ? "Required — used for urgent-ticket WhatsApp alerts." : undefined}
          >
            <Input
              id="phone"
              placeholder="+91 98765 43210"
              value={form.phone}
              onChange={(e) => set("phone")(e.target.value)}
            />
          </Field>
        </div>

        {!isCustomerRole && (
          <fieldset className="space-y-4 rounded-md border border-border p-3">
            <legend className="px-1 text-sm font-medium">Employee details (ID card)</legend>

            {editing && (
              <div className="flex items-center gap-3">
                <UserAvatar
                  userId={editing.id}
                  hasPhoto={editing.has_photo}
                  name={editing.full_name}
                  size={56}
                  refreshKey={photoKey}
                />
                <div className="space-y-1">
                  <label
                    htmlFor="photo"
                    className="inline-flex cursor-pointer items-center rounded-md border border-input px-3 py-1.5 text-sm hover:bg-accent"
                  >
                    {photoBusy ? "Uploading…" : "Upload photo"}
                  </label>
                  <input
                    id="photo"
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handlePhoto(f);
                      e.target.value = "";
                    }}
                  />
                  <p className="text-xs text-muted-foreground">JPEG/PNG/GIF/WebP, up to 5 MB.</p>
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Employee ID" htmlFor="employee_code">
                <Input
                  id="employee_code"
                  value={form.employee_code}
                  onChange={(e) => set("employee_code")(e.target.value)}
                />
              </Field>
              <Field label="Joining date" htmlFor="joining_date">
                <Input
                  id="joining_date"
                  type="date"
                  value={form.joining_date}
                  onChange={(e) => set("joining_date")(e.target.value)}
                />
              </Field>
              <Field label="Date of birth" htmlFor="date_of_birth">
                <Input
                  id="date_of_birth"
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => set("date_of_birth")(e.target.value)}
                />
              </Field>
              <Field label="Blood group" htmlFor="blood_group">
                <Input
                  id="blood_group"
                  placeholder="e.g. O+"
                  value={form.blood_group}
                  onChange={(e) => set("blood_group")(e.target.value)}
                />
              </Field>
              <Field label="Emergency contact" htmlFor="emergency_contact">
                <Input
                  id="emergency_contact"
                  placeholder="+91 98765 43210"
                  value={form.emergency_contact}
                  onChange={(e) => set("emergency_contact")(e.target.value)}
                />
              </Field>
            </div>
            <Field label="Address" htmlFor="address">
              <Input
                id="address"
                value={form.address}
                onChange={(e) => set("address")(e.target.value)}
              />
            </Field>

            <div className="space-y-1.5">
              <div className="text-sm font-medium">Sites</div>
              <p className="text-xs text-muted-foreground">
                Scope this person to specific sites (a Site Admin sees all
                tickets at their sites; an engineer sees their own). Leave all
                unticked for a company-wide account. Administrators always see
                every site.
              </p>
              <div className="max-h-44 space-y-1 overflow-y-auto rounded-md border border-input p-2">
                {(sites ?? []).length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No sites yet — create them under “Sites”.
                  </p>
                )}
                {(sites ?? []).map((s) => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent"
                  >
                    <input
                      type="checkbox"
                      checked={siteIds.includes(s.id)}
                      onChange={(e) =>
                        setSiteIds((prev) =>
                          e.target.checked
                            ? [...prev, s.id]
                            : prev.filter((id) => id !== s.id),
                        )
                      }
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>

            {!editing && (
              <p className="text-xs text-muted-foreground">
                Save the user first, then reopen to add a photo.
              </p>
            )}
          </fieldset>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy || passwordTooShort || phoneMissing}>
            {busy ? "Saving…" : editing ? "Save changes" : "Create user"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
